import apiClient from './api.ts';
import {
    CreateTransitionRequest,
    UpdatePlacementRequest,
    AutoDistributeRequest,
    BulkApprovePlacementsRequest,
    BulkRejectPlacementsRequest,
    PlacementFilters,
    CloneConfig,
    PlacementConfig
} from '@/types/academic_year_transition_schema.ts';

/**
 * Academic Year Transition API Service
 * 
 * Provides methods for all transition-related API endpoints.
 * Base URL: /api/academic-years/transitions
 */
export default {
    // ============================================================
    // Step 1: Prepare Phase
    // ============================================================

    /**
     * Create a new academic year transition
     */
    async createTransition(request: CreateTransitionRequest) {
        return await apiClient.post('academic-years/transitions', request);
    },

    /**
     * Get transition details by ID
     */
    async getTransition(transitionId: number) {
        return await apiClient.get(`academic-years/transitions/${transitionId}`);
    },

    /**
     * List all transitions with optional filters
     */
    async getAllTransitions(
        page: number = 1,
        limit: number = 20,
        status?: string
    ) {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString()
        });
        if (status) {
            params.append('status', status);
        }
        return await apiClient.get(`academic-years/transitions?${params}`);
    },

    /**
     * Validate prerequisites before proceeding
     */
    async validatePrerequisites(transitionId: number, forceProceed: boolean = false) {
        const params = forceProceed ? '?force_proceed=true' : '';
        return await apiClient.get(`academic-years/transitions/${transitionId}/validate${params}`);
    },

    /**
     * Get clone summary (preview what will be cloned)
     */
    async getCloneSummary(transitionId: number) {
        return await apiClient.get(`academic-years/transitions/${transitionId}/clone-summary`);
    },

    /**
     * Update clone configuration
     */
    async updateCloneConfig(transitionId: number, config: CloneConfig) {
        return await apiClient.put(`academic-years/transitions/${transitionId}/clone-config`, config);
    },

    /**
     * Update placement configuration
     */
    async updatePlacementConfig(transitionId: number, config: PlacementConfig) {
        return await apiClient.put(`academic-years/transitions/${transitionId}/placement-config`, config);
    },

    /**
     * Prepare target-year clone records before review/placements.
     */
    async prepareClones(transitionId: number) {
        return await apiClient.post(`academic-years/transitions/${transitionId}/prepare-clones`);
    },

    // ============================================================
    // Step 2: Review Phase
    // ============================================================

    /**
     * Generate transition preview (includes clone mappings)
     */
    async generatePreview(transitionId: number) {
        return await apiClient.post(`academic-years/transitions/${transitionId}/preview`);
    },

    /**
     * Get existing preview
     */
    async getPreview(transitionId: number) {
        return await apiClient.get(`academic-years/transitions/${transitionId}/preview`);
    },

    /**
     * Generate student placements
     */
    async generatePlacements(transitionId: number) {
        return await apiClient.post(`academic-years/transitions/${transitionId}/placements`);
    },

    /**
     * Get all placements with filters
     */
    async getPlacements(transitionId: number, filters: PlacementFilters = {}) {
        const params = new URLSearchParams();
        
        if (filters.page) params.append('page', filters.page.toString());
        if (filters.limit) params.append('limit', filters.limit.toString());
        if (filters.status) params.append('status', filters.status);
        if (filters.grade_id) params.append('grade_id', filters.grade_id.toString());
        if (filters.class_id) params.append('class_id', filters.class_id.toString());
        if (filters.to_class_id) params.append('to_class_id', filters.to_class_id.toString());
        if (filters.from_class_id) params.append('from_class_id', filters.from_class_id.toString());
        if (filters.search) params.append('search', filters.search);
        if (filters.manually_overridden !== undefined) {
            params.append('manually_overridden', filters.manually_overridden.toString());
        }
        if (filters.needs_review !== undefined) {
            params.append('needs_review', filters.needs_review.toString());
        }
        if (filters.issue_type) params.append('issue_type', filters.issue_type);
        
        const queryString = params.toString();
        return await apiClient.get(`academic-years/transitions/${transitionId}/placements${queryString ? '?' + queryString : ''}`);
    },

    /**
     * Get placements by grade
     */
    async getPlacementsByGrade(transitionId: number, gradeId: number) {
        return await apiClient.get(`academic-years/transitions/${transitionId}/placements/grade/${gradeId}`);
    },

    /**
     * Update individual placement (manual override)
     */
    async updatePlacement(transitionId: number, placementId: number, data: UpdatePlacementRequest) {
        return await apiClient.put(`academic-years/transitions/${transitionId}/placements/${placementId}`, data);
    },

    /**
     * Auto-distribute students for a grade
     */
    async autoDistribute(transitionId: number, request: AutoDistributeRequest) {
        const payload = Object.fromEntries(
            Object.entries(request).filter(([, value]) => value !== undefined && value !== null)
        );
        return await apiClient.post(`academic-years/transitions/${transitionId}/placements/auto-distribute`, payload);
    },

    /**
     * Bulk approve placements
     */
    async bulkApprovePlacements(transitionId: number, request: BulkApprovePlacementsRequest) {
        return await apiClient.put(`academic-years/transitions/${transitionId}/placements/bulk-approve`, request);
    },

    /**
     * Bulk reject placements
     */
    async bulkRejectPlacements(transitionId: number, request: BulkRejectPlacementsRequest) {
        return await apiClient.put(`academic-years/transitions/${transitionId}/placements/bulk-reject`, request);
    },

    async generateTeacherAssignments(transitionId: number) {
        return await apiClient.post(`academic-years/transitions/${transitionId}/teacher-assignments/generate`);
    },

    async getTeacherAssignments(transitionId: number, params?: { page?: number; limit?: number }) {
        const query = new URLSearchParams();
        if (params?.page) query.append('page', params.page.toString());
        if (params?.limit) query.append('limit', params.limit.toString());
        const queryString = query.toString();

        return await apiClient.get(`academic-years/transitions/${transitionId}/teacher-assignments${queryString ? '?' + queryString : ''}`);
    },

    async updateTeacherAssignment(transitionId: number, placementId: number, data: any) {
        return await apiClient.put(`academic-years/transitions/${transitionId}/teacher-assignments/${placementId}`, data);
    },

    async bulkApproveTeacherAssignments(transitionId: number) {
        return await apiClient.put(`academic-years/transitions/${transitionId}/teacher-assignments/bulk-approve`, {});
    },

    /**
     * Approve transition (move to Step 3)
     */
    async approveTransition(transitionId: number) {
        return await apiClient.post(`academic-years/transitions/${transitionId}/approve`);
    },

    // ============================================================
    // Step 3: Execute Phase
    // ============================================================

    /**
     * Execute the transition
     */
    async executeTransition(transitionId: number) {
        return await apiClient.post(`academic-years/transitions/${transitionId}/execute`);
    },

    /**
     * Get execution progress (for polling)
     */
    async getProgress(transitionId: number) {
        return await apiClient.get(`academic-years/transitions/${transitionId}/progress`);
    },

    /**
     * Cancel a transition. Allowed in any state except executing, completed,
     * and failed. Deletes placements and prepared clone artifacts.
     */
    async cancelTransition(transitionId: number) {
        return await apiClient.post(`academic-years/transitions/${transitionId}/cancel`);
    },

    /**
     * Rollback a completed transition (within 24 hours)
     */
    async rollbackTransition(transitionId: number) {
        return await apiClient.post(`academic-years/transitions/${transitionId}/rollback`);
    },

    /**
     * Get clone mappings (old → new ID mappings)
     */
    async getCloneMappings(transitionId: number, entityType?: string) {
        const params = entityType ? `?entity_type=${entityType}` : '';
        return await apiClient.get(`academic-years/transitions/${transitionId}/clone-mappings${params}`);
    },

    // ============================================================
    // Historical Data & Archival
    // ============================================================

    /**
     * Get historical academic years
     */
    async getHistoricalYears() {
        return await apiClient.get('academic-years/history');
    },

    /**
     * Get year summary (for cold storage years)
     */
    async getYearSummary(academicYearId: number) {
        return await apiClient.get(`academic-years/${academicYearId}/summary`);
    },

    /**
     * Archive an academic year
     */
    async archiveYear(academicYearId: number) {
        return await apiClient.post(`academic-years/${academicYearId}/archive`);
    },

    /**
     * Restore an archived academic year
     */
    async restoreYear(academicYearId: number) {
        return await apiClient.post(`academic-years/${academicYearId}/restore`);
    },

    // ============================================================
    // SSE Progress Streaming Helper
    // ============================================================

    /**
     * Build SSE URL for progress streaming.
     * Auth goes in the Authorization header (fetch-based stream), never the URL —
     * the backend auth middleware only reads the header, and query tokens leak
     * into access logs.
     */
    buildProgressStreamUrl(transitionId: number): string {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
        return `${baseUrl}/api/v1/academic-years/transitions/${transitionId}/progress/stream`;
    }
};
